import { ElementDatabaseService, TemplateService } from "@/services/admin-services";
import { CreateElementPageConfiguration, ElementPageConfiguration, Template } from "@/services/admin-services/models";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

type ElementPageConfigurationEditorProps = {
    id: string;
    configuration: ElementPageConfiguration | null;
    onChange: (configuration: ElementPageConfiguration) => void;
};

const ElementPageConfigurationEditor = ({ id, configuration, onChange }: ElementPageConfigurationEditorProps) => {
    const [slug, setSlug] = useState<string>("");
    const [slugValid, setSlugValid] = useState<boolean>(true);
    const [template, setTemplate] = useState<string>("");
    const [templates, setTemplates] = useState<Template[]>([]);
    const [pageConfiguration, setPageConfiguration] = useState<ElementPageConfiguration | null>(configuration);

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [savedNotificationOpen, setSavedNotificationOpen] = useState<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const theme = useTheme();

    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);
    const templateService = useMemo(() => new TemplateService("int"), []);

    useEffect(() => {
        templateService.getTemplates().then(setTemplates);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!pageConfiguration) return;
        setSlug(pageConfiguration.slug);
        setTemplate(pageConfiguration.templateId || "");
    }, [pageConfiguration]);

    useEffect(() => {
        setSlugValid(isSlugValid(slug));
    }, [slug]);

    useEffect(() => {
        if (!savedNotificationOpen) return;

        timerRef.current = setTimeout(() => setSavedNotificationOpen(false), 3000);
    }, [savedNotificationOpen]);

    const isSlugValid = (slug: string): boolean => {
        return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
    };

    const onCancel = () => {
        if (!pageConfiguration) return;
        setSlug(pageConfiguration.slug);
        setTemplate(pageConfiguration.templateId || "");
    };

    const onSave = async () => {
        const data: CreateElementPageConfiguration = {
            slug,
            templateId: template.length > 0 ? template : undefined,
        };

        setIsSaving(true);

        let createOrUpdate;

        if (pageConfiguration) {
            createOrUpdate = elementService.updateElementPageConfiguration(id, data);
        } else {
            createOrUpdate = elementService.createElementPageConfiguration(id, data);
        }

        createOrUpdate.then((configuration) => {
            setPageConfiguration({ ...configuration, templateId: template.length > 0 ? template : undefined });
            setSavedNotificationOpen(true);
            setIsSaving(false);
            onChange({ ...configuration, templateId: template.length > 0 ? template : undefined });
        });
    };

    return (
        <Grid container spacing={2} p={{ xs: theme.spacing(0, 2), md: theme.spacing(0, 4) }}>
            <Box sx={{ width: "100%" }}>
                <Collapse in={savedNotificationOpen}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setSavedNotificationOpen(false);
                                }}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                    >
                        Changes to the page information have been succesfully saved.
                    </Alert>
                </Collapse>
            </Box>

            <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                    Page information
                </Typography>
                <Typography variant="subtitle2" color={"text.secondary"}>
                    Define the url and the template for this element page.
                </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Enter a slug for the element
                        </Typography>
                        <TextField
                            label="Slug"
                            variant="outlined"
                            fullWidth
                            value={slug}
                            onChange={(e) => setSlug(e.currentTarget.value)}
                            error={!slugValid}
                            helperText={
                                isSlugValid(slug)
                                    ? ""
                                    : "Invalid format, only lowercase characters, numbers and hypens are allowed."
                            }
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                            Select a template for the page
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="demo-select-small">Template</InputLabel>
                            <Select
                                labelId="demo-select-small"
                                id="demo-select-small"
                                value={template}
                                label="Template"
                                onChange={(e) => setTemplate(e.target.value)}
                                disabled={templates.length === 0}
                            >
                                {templates.map((template) => (
                                    <MenuItem key={template.id} value={template.id}>
                                        {template.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="error"
                            sx={{ ml: "auto" }}
                            onClick={() => onCancel()}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Box sx={{ ml: 1, position: "relative" }}>
                            <Button
                                variant="contained"
                                startIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                                sx={{ ml: 1 }}
                                onClick={() => onSave()}
                                disabled={!isSlugValid(slug) || isSaving}
                            >
                                Save
                            </Button>

                            {isSaving && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ElementPageConfigurationEditor;
